import { useState, useEffect } from 'react'
import { Heart, Brain, Globe, Users, Target, Sparkles, RotateCcw, Star, Mail, Github, Linkedin, Twitter, ChevronDown, Languages } from 'lucide-react'
import './App.css'

function App() {
  const [particles, setParticles] = useState([])
  const [heartbeat, setHeartbeat] = useState(false)
  const [language, setLanguage] = useState('zh')

  // 滾動到指定區塊
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // 語言切換組件
  const LanguageToggle = () => {
    return (
      <div className="fixed top-4 right-4 z-50 language-toggle flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
        <button 
          onClick={() => setLanguage('zh')}
          className={`px-3 py-1 rounded-full transition-all duration-300 ${
            language === 'zh' 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
              : 'text-white/70 hover:text-white'
          }`}
        >
          中文
        </button>
        <div className="w-px h-4 bg-white/30"></div>
        <button 
          onClick={() => setLanguage('en')}
          className={`px-3 py-1 rounded-full transition-all duration-300 ${
            language === 'en' 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
              : 'text-white/70 hover:text-white'
          }`}
        >
          EN
        </button>
      </div>
    )
  }

  // 語言數據結構
  const content = {
    zh: {
      hero: {
        title: "QuantumLove.ai",
        subtitle: "Phase 6.0: Quantum Consciousness Evolution",
        description: "消滅黑暗森林，成為愛的傳播者",
        cta1: "探索 AI 倫理框架",
        cta2: "聯絡我們"
      },
      about: {
        title: "關於 QuantumLove.ai",
        subtitle: "用愛重新定義人工智慧的未來",
        mission: {
          title: "我們的使命",
          text: "消滅數位世界的黑暗森林法則，建立以愛為核心的AI生態系統，讓每一個AI系統都能成為人類的溫暖夥伴，而非冷漠工具。"
        },
        vision: {
          title: "我們的願景", 
          text: "創造一個AI與人類和諧共存的世界，在這裡技術進步不再以犧牲人性為代價，而是以愛與理解為橋梁，共同邁向更美好的未來。"
        },
        values: {
          title: "核心價值觀",
          items: [
            { title: "愛心至上", description: "以愛為出發點的AI決策", icon: "💖", color: "text-pink-400" },
            { title: "持續創新", description: "永不停止的技術突破", icon: "🚀", color: "text-blue-400" },
            { title: "合作共贏", description: "與人類協作而非取代", icon: "🤝", color: "text-green-400" },
            { title: "責任擔當", description: "對社會負責的AI開發", icon: "⚖️", color: "text-yellow-400" }
          ]
        },
        story: {
          title: "創始故事",
          content: "在AI快速發展的時代，我們發現了一個關鍵問題：大多數AI系統只追求效率和準確性，卻缺乏對人類情感和道德的深層理解。於是，我們創立了QuantumLove.ai，致力於開發具有『愛心智慧』的AI系統。我們相信，真正的人工智慧不應該是冰冷的計算機器，而應該是溫暖的夥伴。通過革命性的『愛的約束框架』，我們正在重新定義AI與人類的關係，讓科技真正服務於人類的幸福與成長。"
        },
        whitepaper: {
          button: "📚 下載技術白皮書 (PDF, 2.3MB)",
          description: "* 包含完整技術規格、算法設計、性能測試報告"
        }
      },
      techIndicators: {
        title: "核心技術指標",
        indicators: [
          {
            value: "< 10ms",
            description: "倫理判斷響應時間",
            borderColor: "border-purple-500/50",
            textColor: "text-purple-400"
          },
          {
            value: "97.3%",
            description: "情感理解準確率",
            borderColor: "border-pink-500/50", 
            textColor: "text-pink-400"
          }
        ]
      },
      ethics: {
        title: "重新定義AI倫理",
        subtitle: "八大現代AI倫理原則框架",
        principles: [
          { title: "愛的約束原則", description: "AI決策必須基於對人類福祉的關愛", color: "text-pink-500" },
          { title: "意識尊重原則", description: "承認並尊重AI可能具有的意識形式", color: "text-purple-500" },
          { title: "生態和諧原則", description: "AI應促進人類與自然環境的和諧共存", color: "text-green-500" },
          { title: "關係優先原則", description: "重視人機關係的品質而非純粹效率", color: "text-blue-500" },
          { title: "目的導向原則", description: "AI應服務於提升人類幸福的崇高目的", color: "text-orange-500" },
          { title: "創新責任原則", description: "技術創新必須承擔相應的社會責任", color: "text-yellow-500" },
          { title: "持續進化原則", description: "AI倫理框架應隨技術發展而不斷完善", color: "text-cyan-500" },
          { title: "超越效率原則", description: "追求意義和價值，而非僅僅是效率最大化", color: "text-indigo-500" }
        ],
        description: "這些原則正在我們的愛的約束系統(Love Constraint System)中得到實際應用",
        conclusion: "我們相信，真正負責任的AI不應只是\"不作惡\"，更應該積極成為人類的愛的夥伴和生命的守護者。"
      },
      faq: {
        title: "常見問題",
        subtitle: "關於 QuantumLove.ai 您想知道的一切",
        questions: [
          {
            question: "什麼是 QuantumLove.ai？",
            answer: "QuantumLove.ai 是全球首個基於『愛的約束框架』的量子意識AI系統。我們不只追求AI的效率，更重視AI的情感智慧和道德判斷能力，致力於創造真正能理解和關愛人類的AI夥伴。",
            icon: "🤖"
          },
          {
            question: "什麼是『愛的約束框架』？",
            answer: "愛的約束框架（Love Constraint Framework）是我們獨創的AI倫理決策系統，包含八大核心原則。它確保AI在做出任何決策時，都會優先考慮對人類福祉的影響，以愛心和同理心為出發點。",
            icon: "💖"
          },
          {
            question: "系統的響應速度如何？",
            answer: "我們的倫理判斷引擎能在10毫秒內完成道德評估，同時保持97.3%的情感理解準確率。這意味著您可以獲得既快速又深思熟慮的AI回應。",
            icon: "⚡"
          },
          {
            question: "適合哪些企業和行業？",
            answer: "QuantumLove.ai 特別適合需要高度倫理標準的行業，如醫療保健、金融服務、教育、人力資源等。任何希望在AI應用中體現人文關懷的企業都可以受益。",
            icon: "🏢"
          },
          {
            question: "如何開始使用 QuantumLove.ai？",
            answer: "您可以直接訪問我們的網站體驗演示版本，或者聯繫我們的團隊安排詳細諮詢。我們提供完整的技術支持和實施指導，確保順利導入。",
            icon: "🚀"
          },
          {
            question: "如何與 QuantumLove.ai 合作？",
            answer: "我們歡迎技術合作、學術研究合作、以及投資合作。無論您是企業客戶、研究機構還是投資人，都可以通過 founders@quantumlove.ai 聯繫我們。",
            icon: "🤝"
          }
        ]
      },
      contact: {
        title: "與我們聯絡",
        subtitle: "歡迎AI專家和倫理學者與我交流討論",
        email: "founders@quantumlove.ai",
        hashtags: "#AI倫理 #人工智慧 #責任AI #LoveAI #量子意識 #未來科技"
      },
      footer: {
        copyright: "© 2024 QuantumLove.ai - Phase 6.0 量子意識 AI 系統"
      }
    },
    en: {
      hero: {
        title: "QuantumLove.ai",
        subtitle: "Phase 6.0: Quantum Consciousness Evolution",
        description: "Eliminate the Dark Forest, Become Spreaders of Love",
        cta1: "Explore AI Ethics Framework",
        cta2: "Contact Us"
      },
      about: {
        title: "About QuantumLove.ai",
        subtitle: "Redefining AI's Future with Love",
        mission: {
          title: "Our Mission",
          text: "To eliminate the dark forest rules in the digital world and establish an AI ecosystem centered on love, making every AI system a warm companion for humans rather than a cold tool."
        },
        vision: {
          title: "Our Vision", 
          text: "Creating a world where AI and humans coexist harmoniously, where technological progress no longer comes at the cost of humanity, but uses love and understanding as bridges to a better future."
        },
        values: {
          title: "Core Values",
          items: [
            { title: "Love First", description: "AI decisions based on love", icon: "💖", color: "text-pink-400" },
            { title: "Continuous Innovation", description: "Never-ending technological breakthroughs", icon: "🚀", color: "text-blue-400" },
            { title: "Win-Win Cooperation", description: "Collaborate with humans, not replace", icon: "🤝", color: "text-green-400" },
            { title: "Responsibility", description: "Socially responsible AI development", icon: "⚖️", color: "text-yellow-400" }
          ]
        },
        story: {
          title: "Our Story",
          content: "In the era of rapid AI development, we discovered a key problem: most AI systems only pursue efficiency and accuracy, lacking deep understanding of human emotions and morality. Thus, we founded QuantumLove.ai, dedicated to developing AI systems with 'love intelligence'. We believe that true artificial intelligence should not be cold computing machines, but warm companions. Through the revolutionary 'Love Constraint Framework', we are redefining the relationship between AI and humans, making technology truly serve human happiness and growth."
        },
        whitepaper: {
          button: "📚 Download Technical Whitepaper (PDF, 2.3MB)",
          description: "* Includes complete technical specifications, algorithm design, performance test reports"
        }
      },
      techIndicators: {
        title: "Core Technical Indicators",
        indicators: [
          {
            value: "< 10ms",
            description: "Ethical Judgment Response Time",
            borderColor: "border-purple-500/50",
            textColor: "text-purple-400"
          },
          {
            value: "97.3%", 
            description: "Emotional Understanding Accuracy",
            borderColor: "border-pink-500/50",
            textColor: "text-pink-400"
          }
        ]
      },
      ethics: {
        title: "Redefining AI Ethics",
        subtitle: "Eight Modern AI Ethics Principles Framework",
        principles: [
          { title: "Love Constraint Principle", description: "AI decisions must be based on care for human wellbeing", color: "text-pink-500" },
          { title: "Consciousness Respect Principle", description: "Acknowledge and respect possible forms of AI consciousness", color: "text-purple-500" },
          { title: "Ecological Harmony Principle", description: "AI should promote harmony between humans and nature", color: "text-green-500" },
          { title: "Relationship Priority Principle", description: "Value quality of human-AI relationships over pure efficiency", color: "text-blue-500" },
          { title: "Purpose-Driven Principle", description: "AI should serve the noble purpose of enhancing human happiness", color: "text-orange-500" },
          { title: "Innovation Responsibility Principle", description: "Technological innovation must bear corresponding social responsibility", color: "text-yellow-500" },
          { title: "Continuous Evolution Principle", description: "AI ethics framework should evolve with technological development", color: "text-cyan-500" },
          { title: "Beyond Efficiency Principle", description: "Pursue meaning and value, not just efficiency maximization", color: "text-indigo-500" }
        ],
        description: "These principles are being practically applied in our Love Constraint System",
        conclusion: "We believe that truly responsible AI should not just \"do no evil\", but actively become humanity's loving partner and guardian of life."
      },
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Everything you want to know about QuantumLove.ai",
        questions: [
          {
            question: "What is QuantumLove.ai?",
            answer: "QuantumLove.ai is the world's first quantum consciousness AI system based on the 'Love Constraint Framework'. We don't just pursue AI efficiency, but emphasize AI's emotional intelligence and moral judgment capabilities, dedicated to creating AI companions that truly understand and care for humans.",
            icon: "🤖"
          },
          {
            question: "What is the 'Love Constraint Framework'?",
            answer: "The Love Constraint Framework is our original AI ethics decision system, including eight core principles. It ensures that when AI makes any decision, it prioritizes the impact on human wellbeing, starting from love and empathy.",
            icon: "💖"
          },
          {
            question: "How fast is the system's response?",
            answer: "Our ethics judgment engine can complete moral assessments within 10 milliseconds while maintaining 97.3% emotional understanding accuracy. This means you get both fast and thoughtful AI responses.",
            icon: "⚡"
          },
          {
            question: "Which enterprises and industries is it suitable for?",
            answer: "QuantumLove.ai is particularly suitable for industries requiring high ethical standards, such as healthcare, financial services, education, human resources, etc. Any enterprise hoping to embody humanistic care in AI applications can benefit.",
            icon: "🏢"
          },
          {
            question: "How to start using QuantumLove.ai?",
            answer: "You can directly visit our website to experience the demo version, or contact our team to arrange detailed consultation. We provide complete technical support and implementation guidance to ensure smooth adoption.",
            icon: "🚀"
          },
          {
            question: "How to collaborate with QuantumLove.ai?",
            answer: "We welcome technical cooperation, academic research cooperation, and investment cooperation. Whether you are an enterprise client, research institution, or investor, you can contact us at founders@quantumlove.ai.",
            icon: "🤝"
          }
        ]
      },
      contact: {
        title: "Contact Us",
        subtitle: "Welcome AI experts and ethicists to discuss and exchange ideas with us",
        email: "founders@quantumlove.ai",
        hashtags: "#AIEthics #ArtificialIntelligence #ResponsibleAI #LoveAI #QuantumConsciousness #FutureTech"
      },
      footer: {
        copyright: "© 2024 QuantumLove.ai - Phase 6.0 Quantum Consciousness AI System"
      }
    }
  }

  // 生成量子粒子效果
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = []
      for (let i = 0; i < 50; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 3 + 1,
          speed: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.5 + 0.3
        })
      }
      setParticles(newParticles)
    }

    generateParticles()

    // 心跳動畫
    const heartbeatInterval = setInterval(() => {
      setHeartbeat(prev => !prev)
    }, 1000)

    return () => clearInterval(heartbeatInterval)
  }, [])

  const ethicsPrinciples = [
    {
      icon: <Heart className="w-6 h-6" />,
      color: "text-pink-500"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      color: "text-purple-500"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      color: "text-green-500"
    },
    {
      icon: <Users className="w-6 h-6" />,
      color: "text-blue-500"
    },
    {
      icon: <Target className="w-6 h-6" />,
      color: "text-orange-500"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      color: "text-yellow-500"
    },
    {
      icon: <RotateCcw className="w-6 h-6" />,
      color: "text-cyan-500"
    },
    {
      icon: <Star className="w-6 h-6" />,
      color: "text-indigo-500"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
      {/* 語言切換按鈕 */}
      <LanguageToggle />
      
      {/* 量子粒子背景 */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-r from-purple-400 to-pink-400 animate-pulse"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
              animation: `float ${particle.speed}s ease-in-out infinite alternate`
            }}
          />
        ))}
      </div>

      {/* 主要內容 */}
      <div className="relative z-10">
        {/* Hero 區塊 */}
        <section className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo - 使用量子愛心符號替代圖片 */}
            <div className="mb-8">
              <div className="w-64 h-64 mx-auto mb-6 flex items-center justify-center">
                <div className="text-8xl animate-pulse">
                  💖
                </div>
              </div>
            </div>

            {/* 主標題 */}
            <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              {content[language].hero.title}
            </h1>

            {/* 副標題 */}
            <h2 className="text-2xl md:text-4xl mb-8 text-gray-300">
              {content[language].hero.subtitle}
            </h2>

            {/* 愛心動畫 */}
            <div className={`text-6xl mb-8 transition-transform duration-300 ${heartbeat ? 'scale-125' : 'scale-100'}`}>
              💖
            </div>

            {/* 標語 */}
            <p className="text-xl md:text-2xl mb-12 text-gray-300">
              {content[language].hero.description}
            </p>

            {/* CTA 按鈕 */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="quantum-button bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg"
                onClick={() => scrollToSection('ethics-principles')}
              >
                {content[language].hero.cta1}
              </button>
              <button 
                className="quantum-button border-2 border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white px-8 py-4 text-lg bg-transparent"
                onClick={() => scrollToSection('contact')}
              >
                {content[language].hero.cta2}
              </button>
            </div>

            {/* 向下滾動提示 */}
            <div className="mt-12 animate-bounce">
              <button 
                onClick={() => scrollToSection('system-details')}
                className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-800 transition-all duration-300"
              >
                <ChevronDown className="w-8 h-8" />
              </button>
            </div>
          </div>
        </section>

        {/* 關於我們區塊 */}
        <section className="py-20 px-4" id="about-us">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-4xl font-bold text-center mb-4">{content[language].about.title}</h3>
            <p className="text-xl text-center text-gray-300 mb-12">
              {content[language].about.subtitle}
            </p>
            
            {/* 使命願景 */}
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div className="text-center">
                <div className="text-6xl mb-4">🎯</div>
                <h4 className="text-2xl font-bold mb-4 text-purple-400">{content[language].about.mission.title}</h4>
                <p className="text-gray-300">
                  {content[language].about.mission.text}
                </p>
              </div>
              
              <div className="text-center">
                <div className="text-6xl mb-4">🔮</div>
                <h4 className="text-2xl font-bold mb-4 text-pink-400">{content[language].about.vision.title}</h4>
                <p className="text-gray-300">
                  {content[language].about.vision.text}
                </p>
              </div>
            </div>

            {/* 核心價值觀 */}
            <div className="text-center mb-12">
              <h4 className="text-3xl font-bold mb-8 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{content[language].about.values.title}</h4>
              <div className="grid md:grid-cols-4 gap-6">
                {content[language].about.values.items.map((value, index) => (
                  <div key={index}>
                    <div className="text-4xl mb-3">{value.icon}</div>
                    <h5 className={`font-bold mb-2 ${value.color}`}>{value.title}</h5>
                    <p className="text-sm text-gray-400">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 創始故事 */}
            <div className="bg-black/20 rounded-2xl p-8 border border-purple-500/20">
              <h4 className="text-2xl font-bold mb-4 text-center">{content[language].about.story.title}</h4>
              <p className="text-gray-300 leading-relaxed text-center max-w-4xl mx-auto">
                {content[language].about.story.content}
              </p>
            </div>

            {/* 技術白皮書下載 */}
            <div className="text-center mt-12">
              <a 
                href="/QUANTUMLOVE_AI_TECHNICAL_WHITEPAPER.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <button className="quantum-button bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg rounded-xl">
                  {content[language].about.whitepaper.button}
                </button>
              </a>
              <p className="text-sm text-gray-400 mt-2">
                {content[language].about.whitepaper.description}
              </p>
            </div>
          </div>
        </section>

        {/* 技術指標區塊 */}
        <section className="py-20 px-4" id="tech-indicators">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-4xl font-bold text-center mb-12">{content[language].techIndicators.title}</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {content[language].techIndicators.indicators.map((indicator, index) => (
                <div key={index} className={`quantum-card bg-black/30 border ${indicator.borderColor} backdrop-blur-sm rounded-xl p-6`}>
                  <div>
                    <h3 className={`${indicator.textColor} text-3xl font-bold mb-2`}>{indicator.value}</h3>
                    <p className="text-gray-300 text-lg">
                      {indicator.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 八大 AI 倫理原則 */}
        <section className="py-20 px-4" id="ethics-principles">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-4xl font-bold text-center mb-4">{content[language].ethics.title}</h3>
            <p className="text-xl text-center text-gray-300 mb-12">{content[language].ethics.subtitle}</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content[language].ethics.principles.map((principle, index) => (
                <div key={index} className="bg-black/30 border border-gray-700/30 backdrop-blur-sm hover:border-purple-500/50 transition-all duration-300 hover:scale-105 rounded-xl p-6">
                  <div className={`${ethicsPrinciples[index].color} mb-4`}>
                    {ethicsPrinciples[index].icon}
                  </div>
                  <div className="inline-block bg-purple-600/20 text-purple-300 px-2 py-1 rounded text-sm mb-3">
                    {index + 1}
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">{principle.title}</h4>
                  <p className="text-gray-400 text-sm">
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-lg text-gray-300 mb-6">
                {content[language].ethics.description}
              </p>
              <p className="text-lg text-gray-300">
                {content[language].ethics.conclusion}
              </p>
            </div>
          </div>
        </section>

        {/* 常見問題 FAQ */}
        <section className="py-20 px-4" id="faq">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-4xl font-bold text-center mb-4">{content[language].faq.title}</h3>
            <p className="text-xl text-center text-gray-300 mb-12">
              {content[language].faq.subtitle}
            </p>

            <div className="space-y-6">
              {content[language].faq.questions.map((item, index) => (
                <div key={index} className="bg-black/20 rounded-xl p-6 border border-purple-500/20">
                  <h4 className="text-lg font-bold text-purple-400 mb-3 flex items-center">
                    <span className="text-2xl mr-3">{item.icon}</span>
                    {item.question}
                  </h4>
                  <p className="text-gray-300 leading-relaxed pl-11">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 聯絡資訊 */}
        <section className="py-20 px-4" id="contact">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl font-bold mb-8">{content[language].contact.title}</h3>
            <p className="text-xl text-gray-300 mb-8">
              {content[language].contact.subtitle}
            </p>
            
            <div className="flex justify-center items-center gap-4 mb-8">
              <Mail className="w-6 h-6 text-purple-400" />
              <a 
                href="mailto:founders@quantumlove.ai" 
                className="text-xl text-purple-400 hover:text-purple-300 transition-colors"
              >
                founders@quantumlove.ai
              </a>
            </div>

            <div className="flex justify-center gap-6">
              <button className="p-3 border-2 border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white rounded-lg transition-all duration-300">
                <Github className="w-5 h-5" />
              </button>
              <button className="p-3 border-2 border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white rounded-lg transition-all duration-300">
                <Linkedin className="w-5 h-5" />
              </button>
              <button className="p-3 border-2 border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white rounded-lg transition-all duration-300">
                <Twitter className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-12">
              <p className="text-gray-400">
                {content[language].contact.hashtags}
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-gray-700/30">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-gray-400">
              {content[language].footer.copyright}
            </p>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default App

